import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createStore } from "polotno/model/store";
import { PolotnoSlideshowViewer } from "../components/PolotnoSlideshowViewer";
import { VideoViewer } from "../components/VideoViewer";
import { WorkflowSteps } from "../components/WorkflowSteps";
import { getTemplateById } from "../api/templates";
import { rebuildPolotnoJsonSafely } from "../utils/polotnoJsonLoader";
import "./PreviewPage.css";

function isLikelyVideoUrl(url = "") {
  return /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i.test(String(url));
}

export const PreviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const jsonDataFromEditor = location.state?.jsonData;
  const templateId = location.state?.templateId || params.Id;
  const eventId = location.state?.eventId;
  const mode = location.state?.mode;
  const slideshowImagesFromState = location.state?.slideshowImages || [];
  const returnTo = location.state?.returnTo;
  const returnLabel = location.state?.returnLabel;
  const returnState = location.state?.returnState;
  const navigateUrl = "/editor/" + templateId;

  const [tab, setTab] = useState("slideshow");
  const [store] = useState(() => createStore({ key: "demo-key" }));

  const [jsonData, setJsonData] = useState(jsonDataFromEditor || null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [slideshowImages] = useState(slideshowImagesFromState);
  const [isLoading, setIsLoading] = useState(!jsonDataFromEditor);
  const editorReturnState = {
    mode,
    eventId,
    jsonData: jsonDataFromEditor || jsonData,
  };
  const isAdminTemplatePreview = mode === "template" && !eventId;
  const fallbackReturnTo = isAdminTemplatePreview ? "/admin/templates" : "/";
  const fallbackReturnLabel = "Back";
  const isEditorBackFlow = Boolean(jsonDataFromEditor || eventId || (returnTo == null && !isAdminTemplatePreview));

  useEffect(() => {
    const fetchTemplateData = async () => {
      if (!jsonDataFromEditor && templateId) {
        setIsLoading(true);
        try {
          const tpl = await getTemplateById(templateId);
          const templateJson = JSON.parse(tpl.htmltext);
          setJsonData(templateJson);
          setVideoUrl(tpl.preview_video_url || (isLikelyVideoUrl(tpl.background_image) ? tpl.background_image : null));
          setImageUrl(tpl.thumbnail_image || (!isLikelyVideoUrl(tpl.background_image) ? tpl.background_image : null));
          if (!rebuildPolotnoJsonSafely(store, templateJson)) {
            await Promise.resolve(store.loadJSON(templateJson));
          }
          if (store.pages?.[0]?.id) {
            store.selectPage(store.pages[0].id);
          }
        } catch (err) {
          console.error("Failed to fetch template:", err);
        } finally {
          setIsLoading(false);
        }
      } else if (jsonDataFromEditor) {
        setVideoUrl(null);
        setImageUrl(null);
        if (!rebuildPolotnoJsonSafely(store, jsonDataFromEditor)) {
          await Promise.resolve(store.loadJSON(jsonDataFromEditor));
        }
        if (store.pages?.[0]?.id) {
          store.selectPage(store.pages[0].id);
        }
      }
    };
    fetchTemplateData();
  }, [templateId, jsonDataFromEditor, store]);

  if (isLoading) {
    return (
      <div className="preview-loader">
        <p>Loading preview...</p>
      </div>
    );
  }

  if (!jsonData) {
    return (
      <div className="preview-loader">
        <p>No template data found.</p>
        <button
          onClick={() =>
            isEditorBackFlow
              ? navigate(navigateUrl, { state: editorReturnState })
              : navigate(returnTo || fallbackReturnTo, { state: returnState })
          }
          className="preview-secondary-btn fallback"
        >
          {isEditorBackFlow ? "Back to Editor" : returnLabel || fallbackReturnLabel}
        </button>
      </div>
    );
  }

  const isEditMode = mode === "template" || mode === "event";
  const pageCount = jsonData?.pages?.length || 0;
  const currentViewLabel = tab === "slideshow" ? "Slideshow Preview" : "Video Preview";

  const renderContent = () => {
    switch (tab) {
      case "slideshow":
        return (
          <div className="viewer-container">
            <PolotnoSlideshowViewer
              slides={slideshowImages.length ? slideshowImages : imageUrl ? [imageUrl] : []}
              jsonData={jsonData}
              interval={4000}
              fadeDuration={800}
              showDots={true}
            />
          </div>
        );
      case "video":
        return jsonDataFromEditor ? (
          <VideoViewer store={store} templateId={templateId} mode={mode} eventId={eventId} />
        ) : (
          <div className="media-center">
            {videoUrl ? (
              <video src={videoUrl} controls className="media-preview" />
            ) : imageUrl ? (
              <img src={imageUrl} alt="Template preview" className="media-preview image-preview" />
            ) : (
              <p>No media preview available</p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="preview-page">
      <div className="preview-shell">
        {isEditMode ? <WorkflowSteps currentStep="preview" /> : null}

        <header className="preview-header">
          <div className="preview-header-copy">
            <p className="preview-kicker">{isEditMode ? "Review Step" : "Template Preview"}</p>
            <h1>{isEditMode ? "Review Invitation" : "Explore Template"}</h1>
          </div>

          <div className="preview-header-actions">
            <button
              className="preview-secondary-btn"
              onClick={() =>
                isEditorBackFlow
                  ? navigate(navigateUrl, { state: editorReturnState })
                  : navigate(returnTo || fallbackReturnTo, { state: returnState })
              }
            >
              {isEditorBackFlow ? "Back to Editor" : returnLabel || fallbackReturnLabel}
            </button>
            {isAdminTemplatePreview && (
              <button
                className="preview-secondary-btn"
                onClick={() => navigate(returnTo || fallbackReturnTo, { state: returnState })}
              >
                Templates
              </button>
            )}
            {eventId && (
              <button
                className="preview-primary-btn"
                onClick={() => navigate("/event-manager", { state: { eventId } })}
              >
                Continue to Finalize Details
              </button>
            )}
          </div>
        </header>

        <section className="preview-summary-grid">
          <div className="preview-summary-card">
            <span className="preview-summary-label">Current View</span>
            <strong>{currentViewLabel}</strong>
          </div>
          <div className="preview-summary-card">
            <span className="preview-summary-label">Pages</span>
            <strong>{pageCount || "Unknown"}</strong>
          </div>
          <div className="preview-summary-card">
            <span className="preview-summary-label">Mode</span>
            <strong>{isEditMode ? "Editor Review" : "Saved Template"}</strong>
          </div>
        </section>

        <section className="preview-stage">
          <div className="preview-stage-toolbar">
            <div className="tab-bar">
              {["slideshow", "video"].map((t) => (
                <button
                  key={t}
                  className={`tab-btn ${tab === t ? "active" : ""}`}
                  onClick={() => setTab(t)}
                >
                  {t === "slideshow" && "Slideshow"}
                  {t === "video" && "Video"}
                </button>
              ))}
            </div>
            {tab !== "video" ? (
              <button className="preview-video-cta" onClick={() => setTab("video")} type="button">
                Generate Video Preview
              </button>
            ) : null}
          </div>

          <div className="tab-content">{renderContent()}</div>
        </section>
      </div>
    </div>
  );
};
